import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  ConnectJobTaxDataImportRequest,
  GeneratePublicAccessTokenRequest,
  GetJobTaxDataImportRequest,
  InitializeJobTaxDataImportRequest,
  MeasureOneDataRequestItemSelection,
  StartDocumentsUploadRequest,
} from '@taxfyle/api-internal/internal/measure_one_pb'
import {
  mapMeasureOneExperienceToProtos,
  mapMeasureOneSourceDataToProtos,
  mapTaxJobDataImport,
  type MeasureOneExperience,
  type WellKnownDocumentTypeShortId,
} from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import { filter, Subject } from 'rxjs'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { collection } from '@/infra/collection'
import { JobTaxDataImport } from '@/features/jobs/details/tax-type/info-gathering/measure-one/JobTaxDataImport'
import { filterNonNil } from '@/utils/rx-util'
import type { AvailableItem } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView/SelectDocumentsView'
import { Id } from '@/infra/id'
import { groupBy } from 'lodash-es'

export class MeasureOneStore {
  private readonly _uploadCompleted$ = new Subject<Id>()

  readonly taxJobData = collection({
    create: () => new JobTaxDataImport(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  /**
   * Emits when a upload completed.
   */
  readonly uploadCompleted$ = this._uploadCompleted$.asObservable()

  constructor(private readonly _api: JobsApi) {
    this._api.realtime$
      .pipe(
        filter((event) =>
          [
            RealtimeEvent.TypeCase.MEASURE_ONE_JOB_TAX_DATA_IMPORT_UPDATED,
            RealtimeEvent.TypeCase
              .MEASURE_ONE_JOB_TAX_DATA_IMPORT_UPLOAD_COMPLETED,
          ].includes(event.getTypeCase()),
        ),
        filterNonNil(),
      )
      .subscribe(this._onRemoteEvent.bind(this))
  }

  async initializeJobTaxDataImport(
    jobId: string,
    workspaceId: string,
    experience: MeasureOneExperience,
    documentTypes: WellKnownDocumentTypeShortId[],
  ) {
    const request = new InitializeJobTaxDataImportRequest()
      .setJobId(jobId)
      .setWorkspaceId(workspaceId)
      .setExperienceType(mapMeasureOneExperienceToProtos(experience))
      .setSourceDataTypesList(
        documentTypes.map((documentType) =>
          mapMeasureOneSourceDataToProtos(documentType),
        ),
      )

    const response =
      await this._api.measureOne.initializeJobTaxDataImport(request)

    return response.getDataRequestId()
  }

  async generatePublicAccessToken(jobId: string) {
    const request = new GeneratePublicAccessTokenRequest().setJobId(jobId)

    const response =
      await this._api.measureOne.generatePublicAccessToken(request)

    return response.getAccessToken()
  }

  async connectJobTaxDataImport(
    dataRequestId: string,
    dataSourceId: string,
    dataSourceName: string,
    jobId: string,
  ) {
    const request = new ConnectJobTaxDataImportRequest()
      .setDataRequestId(dataRequestId)
      .setDataSourceId(dataSourceId)
      .setDataSourceName(dataSourceName)
      .setJobId(jobId)

    await this._api.measureOne.connectJobTaxDataImport(request)
  }

  async getJobTaxDataImport(jobId: string) {
    const request = new GetJobTaxDataImportRequest().setJobId(jobId)

    return this._api.measureOne
      .getJobTaxDataImport(request)
      .then((response) => this.taxJobData.set(mapTaxJobDataImport(response)))
  }

  async startDocumentsUpload(jobId: string, selectedItems: AvailableItem[]) {
    const dataRequestGroupedItems = Object.entries(
      groupBy(selectedItems, 'dataRequestId'),
    ).map(([dataRequestId, items]) =>
      new MeasureOneDataRequestItemSelection()
        .setDataRequestId(dataRequestId)
        .setItemIdsList(items.map((item) => Id.assert(item.id))),
    )

    const request = new StartDocumentsUploadRequest()
      .setJobId(jobId)
      .setDataRequestsList(dataRequestGroupedItems)

    return this._api.measureOne.startDocumentsUpload(request)
  }

  private _onRemoteEvent(e: RealtimeEvent) {
    switch (e.getTypeCase()) {
      case RealtimeEvent.TypeCase.MEASURE_ONE_JOB_TAX_DATA_IMPORT_UPDATED: {
        const event = e.getMeasureOneJobTaxDataImportUpdated()
        const proto = event?.getJobTaxDataImport()

        if (proto) {
          this.taxJobData.set(mapTaxJobDataImport(proto))
        }

        break
      }
      case RealtimeEvent.TypeCase
        .MEASURE_ONE_JOB_TAX_DATA_IMPORT_UPLOAD_COMPLETED: {
        const event = e.getMeasureOneJobTaxDataImportUploadCompleted()
        const proto = event?.getJobTaxDataImport()

        if (proto) {
          this._uploadCompleted$.next(Id.assert(proto.getJobId()))
        }

        break
      }
      default:
        break
    }
  }
}
